var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import AdminPageLayout from '../../../layout/admin-page-layout';
import Input from '@rad/components/inputs/input';
import RadioGroup from '@rad/components/inputs/radio-group';
import CheckSwitch from '@rad/components/inputs/checkswitch';
import Modal from '@rad/components/ui/modal';
import createTable from '@rad/components/ui/table';
import { useFetch, usePromise } from '@rad/hooks';
import ActionButton from '@rad/components/ui/action-button';
import { expand } from '@rad/utility/validation';
import { getBanners, createBanner, updateBanner, deleteBanner, getBannerVariants } from '../../../common/network/banner';
import { createBannerState } from '../../../types/banner';
import { useToast } from '../../../contexts/toast';
const AdminContentBanners = () => {
    var _a;
    const toast = useToast();
    const [bannersPromise, refetchBanners] = useFetch(getBanners());
    const [variantsPromise] = useFetch(getBannerVariants());
    const [showModal, setShowModal] = React.useState(false);
    const [editingId, setEditingId] = React.useState(null);
    const [form, setForm] = React.useState(createBannerState());
    const [errors, setErrors] = React.useState({});
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [deletingId, setDeletingId] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [activeFilter, setActiveFilter] = React.useState('active');
    const [sort, setSort] = React.useState({ field: 'sort_order', direction: 'asc' });
    const banners = usePromise(bannersPromise);
    const filteredBanners = (banners || [])
        .filter(banner => activeFilter !== 'active' || banner.active)
        .filter(banner => !searchTerm || banner.message.toLowerCase().includes(searchTerm.toLowerCase()));
    const BannersTable = createTable(Promise.resolve(filteredBanners));
    const openAddModal = () => {
        setEditingId(null);
        setForm(createBannerState());
        setErrors({});
        setShowModal(true);
    };
    const openEditModal = (banner) => {
        setEditingId(banner.id);
        const { variant } = banner, rest = __rest(banner, ["variant"]);
        setForm(Object.assign(Object.assign({}, rest), { variant_name: variant.name }));
        setErrors({});
        setShowModal(true);
    };
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        setErrors({});
        const [success, err] = editingId
            ? yield updateBanner(editingId, form)
            : yield createBanner(form);
        if (success) {
            setShowModal(false);
            refetchBanners();
            toast.success(editingId ? 'Banner updated' : 'Banner created');
        }
        else {
            const [, expanded] = expand(err);
            setErrors(expanded);
            toast.error('Please fix the errors below');
        }
    });
    const handleDeleteClick = (id) => {
        setDeletingId(id);
        setShowDeleteModal(true);
    };
    const handleDeleteConfirm = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!deletingId)
            return;
        yield deleteBanner(deletingId);
        setShowDeleteModal(false);
        setDeletingId(null);
        refetchBanners();
        toast.success('Banner deleted');
    });
    const getVariantBadge = (variant) => {
        const colors = {
            beta: 'bg-primary',
            dev: 'bg-secondary',
            warning: 'bg-warning',
            info: 'bg-info',
        };
        return _jsx("span", { className: `badge ${colors[variant.name] || 'bg-secondary'}`, children: variant.title });
    };
    const activeCount = banners ? banners.filter(b => b.active).length : 0;
    const filterOptions = Promise.resolve([
        { value: 'active', label: `Active ${activeCount}` },
        { value: 'all', label: `All ${(_a = banners === null || banners === void 0 ? void 0 : banners.length) !== null && _a !== void 0 ? _a : 0}` }
    ]);
    return (_jsx(AdminPageLayout, { children: _jsxs("div", { className: "container py-4", children: [_jsx("h5", { className: "fw-semibold mb-3", children: "Banners" }), _jsxs("div", { className: "d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4", children: [_jsxs("div", { className: "d-flex align-items-center gap-2 flex-wrap", children: [_jsx(RadioGroup, { value: activeFilter, onChange: (val) => setActiveFilter(val || 'active'), options: filterOptions, optionKey: (opt) => opt.value, optionLabel: (opt) => opt.label, buttonGroupClassName: "btn-group", buttonClassName: (opt) => `btn ${activeFilter === opt.value ? 'btn-primary' : 'btn-outline-secondary'}` }), _jsx("div", { style: { width: 240 }, children: _jsx(Input, { kind: "text", value: searchTerm, onChange: setSearchTerm, label: "Search..." }) })] }), _jsxs("button", { className: "btn btn-primary", onClick: openAddModal, children: [_jsx("i", { className: "bi bi-plus-lg me-2" }), "Add Banner"] })] }), _jsx(BannersTable, { className: "table-bordered", sort: sort, onSortChange: setSort, columns: [
                        { header: 'Variant', render: (b) => getVariantBadge(b.variant) },
                        {
                            header: 'Message',
                            sortField: 'message',
                            render: (b) => _jsx("div", { className: "text-truncate", style: { maxWidth: 400 }, children: b.message })
                        },
                        { header: 'Sort Order', sortField: 'sort_order', render: (b) => b.sort_order },
                        {
                            header: 'Color',
                            render: (b) => (_jsxs("div", { className: "d-flex align-items-center gap-2", children: [_jsx("div", { style: { width: 24, height: 24, backgroundColor: b.color, border: '1px solid #dee2e6', borderRadius: 4 } }), _jsx("span", { className: "font-monospace small", children: b.color })] }))
                        },
                        {
                            header: 'Active',
                            sortField: 'active',
                            render: (b) => b.active ? _jsx("span", { className: "badge bg-success", children: "Active" }) : _jsx("span", { className: "badge bg-secondary", children: "Inactive" })
                        },
                        {
                            header: 'Dismissible',
                            render: (b) => b.dismissible ? _jsx("i", { className: "bi bi-check-lg text-success" }) : _jsx("i", { className: "bi bi-x-lg text-muted" })
                        },
                        {
                            header: 'Actions',
                            className: 'text-center',
                            render: (b) => (_jsxs(_Fragment, { children: [_jsx("button", { className: "btn btn-link text-primary p-0 me-2", onClick: (e) => { e.stopPropagation(); openEditModal(b); }, title: "Edit", children: _jsx("i", { className: "bi bi-pencil" }) }), _jsx("button", { className: "btn btn-link text-danger p-0", onClick: (e) => { e.stopPropagation(); handleDeleteClick(b.id); }, title: "Delete", children: _jsx("i", { className: "bi bi-trash" }) })] }))
                        }
                    ] }), _jsx(Modal, { isOpen: showModal, title: editingId ? 'Edit Banner' : 'Add Banner', onClose: () => setShowModal(false), onSave: handleSave, children: _jsxs("div", { className: "d-flex flex-column gap-3", children: [_jsx(RadioGroup, { label: "Variant", value: form.variant_name, onChange: (val) => setForm(Object.assign(Object.assign({}, form), { variant_name: val || 'info' })), options: variantsPromise, optionKey: (opt) => opt.name, optionLabel: (opt) => opt.title, buttonGroupClassName: "btn-group w-100", buttonClassName: (opt) => `btn ${form.variant_name === opt.name ? 'btn-primary' : 'btn-outline-secondary'}`, invalid: errors.variant_name }), _jsx(Input, { kind: "textarea", label: "Message", value: form.message, onChange: (v) => setForm(Object.assign(Object.assign({}, form), { message: v })), invalid: errors.message }), _jsx(Input, { kind: "text", label: "Sort Order", value: form.sort_order.toString(), onChange: (v) => setForm(Object.assign(Object.assign({}, form), { sort_order: parseInt(v) || 0 })), invalid: errors.sort_order }), _jsx(Input, { kind: "color", label: "Color", value: form.color, onChange: (v) => setForm(Object.assign(Object.assign({}, form), { color: v || '#000000' })), invalid: errors.color }), _jsx(CheckSwitch, { switch: false, label: "Active", value: form.active, onChange: (v) => setForm(Object.assign(Object.assign({}, form), { active: v })) }), _jsx(CheckSwitch, { switch: false, label: "Dismissible", value: form.dismissible, onChange: (v) => setForm(Object.assign(Object.assign({}, form), { dismissible: v })) }), _jsx(CheckSwitch, { switch: false, label: "Show Feedback Link", value: form.show_feedback_link, onChange: (v) => setForm(Object.assign(Object.assign({}, form), { show_feedback_link: v })) }), errors.form && _jsx("div", { className: "alert alert-danger", children: errors.form.join(', ') })] }) }), _jsx(Modal, { isOpen: showDeleteModal, title: "Delete Banner", onClose: () => setShowDeleteModal(false), footer: _jsx(ActionButton, { className: "btn btn-danger", onClick: handleDeleteConfirm, loadingText: "Deleting...", children: "Delete" }), children: _jsx("p", { children: "Are you sure you want to delete this banner? This action cannot be undone." }) })] }) }));
};
export default AdminContentBanners;
